import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useRef } from 'react';
import { formatPlaceName, isDuplicatePlaceNameCheck, PlaceTypeEnum, } from '@aviv_price-pages/brand';
import { Box } from '@gemini/core';
import { MagnifyingGlassIcon } from '@gemini/icons';
import { Autocomplete } from '@gemini/ui';
import { Error404Illu } from '@/assets/icons/Error404Illu';
import { Error500Illu } from '@/assets/icons/Error500Illu';
import { getActivePlaceTypes } from '@/helpers/geo/places';
import { useMobileViewDetect } from '@/hooks/useMobileViewDetect/useMobileViewDetect';
import { usePageLayoutContext } from '@/hooks/usePageLayoutContext/usePageLayoutContext';
import { FeedbackSubtype, usePlaceSearch } from '@/hooks/usePlaceSearch/usePlaceSearch';
import { useShellTranslation } from '@/hooks/useShellTranslation/useShellTranslation';
const Searchbox = () => {
    const { t } = useShellTranslation();
    const inputRef = useRef(null);
    const { state: { search, places, isLoading, searchUnderlayOpen, error }, onFocusChange, handleChange, onSelectionChange, sendFeedback, } = usePlaceSearch(getActivePlaceTypes());
    const { isMobileView } = useMobileViewDetect();
    const { pricePageProps: { pricePageContext }, } = usePageLayoutContext();
    useEffect(() => {
        const activeElement = inputRef.current;
        const onBlur = (event) => {
            onFocusChange(event.type === 'focus');
        };
        const onKeyDown = (event) => {
            if ((event.key === 'Enter' && isLoading) || event.key === 'Tab') {
                event.stopPropagation();
            }
        };
        activeElement?.addEventListener('blur', onBlur);
        activeElement?.addEventListener('focus', onBlur);
        activeElement?.addEventListener('keydown', onKeyDown);
        return () => {
            activeElement?.removeEventListener('blur', onBlur);
            activeElement?.removeEventListener('focus', onBlur);
            activeElement?.removeEventListener('keydown', onKeyDown);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [inputRef.current, isLoading]);
    const handleSelected = (_value, id) => {
        if (id === null) {
            return;
        }
        const placeIndex = places.findIndex((item) => item.id === id);
        const place = places[placeIndex];
        if (place) {
            sendFeedback(search || '', FeedbackSubtype.COMPLETED, placeIndex + 1, place.id);
            onSelectionChange(place);
        }
    };
    useEffect(() => {
        if (document.activeElement instanceof HTMLElement) {
            document.activeElement.blur();
        }
    }, [pricePageContext]);
    let emptyState = {
        title: t('search.dropdown.no_result_title'),
        description: t('search.dropdown.no_result_description'),
        image: Error404Illu,
    };
    const buildDropdownContent = () => {
        if (error) {
            emptyState = {
                title: t('search.dropdown.error_title'),
                description: t('search.dropdown.error_description'),
                image: Error500Illu,
            };
            return [];
        }
        // empty search hide dropdown
        if (!search) {
            return [];
        }
        // // show loading indicator
        if (isLoading) {
            emptyState = {
                title: '',
                description: '',
            };
            return [];
        }
        // format places for displaying
        return places.map((place) => {
            const isDuplicatePlaceName = isDuplicatePlaceNameCheck(place, places);
            const value = [
                PlaceTypeEnum.CITY,
                PlaceTypeEnum.STREET,
                PlaceTypeEnum.NEIGHBORHOOD,
            ].includes(place.type)
                ? formatPlaceName(place, {
                    withPreposition: false,
                    withProvince: isDuplicatePlaceName,
                })
                : place.name.display ?? '';
            return {
                id: place.id,
                label: value,
                suffix: t(`search.dropdown.${place.type}`),
            };
        });
    };
    return (_jsxs(_Fragment, { children: [_jsx(Autocomplete, { value: search, label: {
                    content: t('search.mobile.header_title'),
                    isHidden: true,
                }, hasNecessityIndicator: false, items: buildDropdownContent(), onChange: handleChange, onSubmit: handleSelected, iconStart: MagnifyingGlassIcon, defaultItems: undefined, placeholder: t('search.input.placeholder'), zIndex: "zIndex.2", ref: inputRef, emptyState: search ? emptyState : undefined, testId: "autocomplete" }), searchUnderlayOpen && !isMobileView && (_jsx(Box, { position: "fixed", top: "0", left: "0", bottom: "0", right: "0", zIndex: "zIndex.1", backgroundColor: "color.background.backdrop.default" }))] }));
};
export default Searchbox;
